import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Box, Stack, Button } from "@chakra-ui/react";
import { useCardProvider } from "utils";
import BankAccountList from "./BankAccountList";
import { AddPaypalButton } from "components/Paypal";
import { useGenerateUrlAndRedirectUser } from "containers/Pages/PlaidACH/useGenerateUrlAndRedirectUser";
import BankAccountForm from "./BankAccountForm";

/**
 * Component for adding payment method content.
 * @param {Object} props - The props of the component.
 * @param {Function} props.onComplete - Callback function when adding payment method is complete.
 * @param {Function} props.onCancel - Callback function when adding payment method is canceled.
 * @param {Object} [props.chooserConfig={}] - Configuration for payment method chooser.
 * @param {Object} [props.paymentMethodDescriptions={}] - Descriptions for payment methods.
 * @param {string} props.description - Description of the payment method.
 * @param {string} props.path - Path for navigation.
 * @param {Function} [props.onBankAccountClick] - Optional.  Handle bank account selection by user
 * @returns {JSX.Element} Rendered component.
 */
const AddPaymentMethodContent = ({
  onComplete,
  onCancel,
  chooserConfig = {},
  providerDescriptions = {},
  description,
  path,
  onBankAccountClick,
}) => {
  const { Provider: AddCard } = useCardProvider();
  const { addACH = false, addCC = false, addPaypal = false } = chooserConfig;
  const [selectedBankAccountCountry, setSelectedBankAccountCountry] = useState(null);
  const history = useHistory();
  const { onGenerateUrlAndRedirectUser } = useGenerateUrlAndRedirectUser();

  /**
   * Handles bank country selection.
   * @param {Object} countryOption - The selected country option.
   */
  const handleBankCountrySelect = (countryOption) => {
    setSelectedBankAccountCountry(countryOption);
    const type = countryOption.type;
    if (type === "plaid") {
      onGenerateUrlAndRedirectUser();
    }
  };

  if (selectedBankAccountCountry && selectedBankAccountCountry.type === "masspay-form") {
    if (path) {
      setTimeout(() => {
        // moving this to next-tick to avoid a state update error issue
        history.push({
          pathname: `${path}/masspay-form`,
          state: {
            countryISO: selectedBankAccountCountry.ISO,
            currency: selectedBankAccountCountry.currency,
          },
        });
      }, 0);
    } else {
      return (
        <BankAccountForm
          countryISO={selectedBankAccountCountry.ISO}
          currency={selectedBankAccountCountry.currency}
        />
      );
    }
  }

  return (
    <Box py="10">
      {addCC ? (
        <AddCard onComplete={onComplete} onCancel={onCancel} config={chooserConfig} />
      ) : (
        <Box>
          <Box>{description}</Box>
          <Stack spacing="4" mt="7">
            {addPaypal && (
              <AddPaypalButton newUI={true} description={providerDescriptions.paypal} />
            )}
            {addACH && (
              <BankAccountList
                onCountryChange={handleBankCountrySelect}
                onBankAccountClick={onBankAccountClick}
                description={providerDescriptions.banking}
              />
            )}
          </Stack>
          <Box mt="16">
            <Button data-testid="add-pm-cancel-button" onClick={onCancel} width="100%">
              <FormattedMessage defaultMessage="Cancel" description="button copy" />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddPaymentMethodContent;
